import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { ContentHeader } from '@/components/content-header';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Button,
  FieldSeparator,
  UnsavedChangesConfirmDialog,
  useFormDialogWithUnsavedChanges
} from '@bliks-io/ui';
import { FormInput, FormTextarea } from '@/components/form/rhf';
import {
  BulkValidateDepartmentInputSchema,
  type BulkValidateDepartmentInput
} from '@bliks-io/contracts';
import { useDepartments } from '@/api/queries/useDepartments';
import { ParentAbbreviationComboBox } from './ParentAbbreviationComboBox';

interface ManualAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Resolves `true` once the department is in the validated batch; the dialog stays open on `false`. */
  onAdd: (data: BulkValidateDepartmentInput) => Promise<boolean>;
  initialData?: BulkValidateDepartmentInput;
  isEditing?: boolean;
  existingAbbreviations?: string[]; // Abbreviations from current batch
}

export function ManualAddDialog({
  open,
  onOpenChange,
  onAdd,
  initialData,
  isEditing = false,
  existingAbbreviations = []
}: ManualAddDialogProps) {
  const { t } = useTranslation();
  const { data: departments } = useDepartments();

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isSubmitting }
  } = useForm<BulkValidateDepartmentInput>({
    resolver: zodResolver(BulkValidateDepartmentInputSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      abbreviation: '',
      description: undefined,
      parentAbbreviation: undefined,
      managerEmail: undefined
    }
  });

  // Unsaved changes handling
  const { showCloseConfirmation, handleOpenChange, handleDiscardChanges, handleStay } =
    useFormDialogWithUnsavedChanges({
      isDirty,
      onClose: () => onOpenChange(false)
    });

  useEffect(
    function resetFormWhenDialogOpens() {
      if (open) {
        reset({
          name: initialData?.name || '',
          abbreviation: initialData?.abbreviation || '',
          description: initialData?.description || undefined,
          parentAbbreviation: initialData?.parentAbbreviation || undefined,
          managerEmail: initialData?.managerEmail || undefined
        });
      }
    },
    [open, initialData, reset]
  );

  const handleFormSubmit = async (data: BulkValidateDepartmentInput) => {
    if (!(await onAdd(data))) return;
    if (!isEditing) {
      reset();
    }
    onOpenChange(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!isSubmitting) handleOpenChange(nextOpen);
        }}
      >
        <DialogContent className="max-w-3xl p-6">
          <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
            <DialogHeader className="mb-10">
              <ContentHeader
                mode="dialog"
                variant="large"
                icon={ENTITY_ICONS.department}
                title={
                  isEditing
                    ? t('organization.departments.bulkUploadPage.editDraft')
                    : t('organization.departments.bulkUploadPage.addManually')
                }
                description={
                  isEditing
                    ? t('organization.departments.bulkUploadPage.editDraftDescription')
                    : t('organization.departments.bulkUploadPage.addManuallyDescription')
                }
              />
            </DialogHeader>

            <div className="flex flex-col gap-8">
              {/* Basic Information Section */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormInput
                  control={control}
                  name="name"
                  label={t('common.Name')}
                  placeholder={t('organization.departments.namePlaceholder')}
                  required
                />

                <FormInput
                  control={control}
                  name="abbreviation"
                  label={t('organizations.Abbreviation')}
                  placeholder={t('organization.departments.abbreviation')}
                  required
                />
              </div>

              <div className="flex flex-col gap-4">
                <FieldSeparator>{t('organization.departments.relationships')}</FieldSeparator>

                {/* Parent Abbreviation - Special Combo Box */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <ParentAbbreviationComboBox
                    control={control}
                    name="parentAbbreviation"
                    label={t('organization.departments.parentDepartment')}
                    placeholder={t(
                      'organization.departments.bulkUploadPage.parentComboPlaceholder'
                    )}
                    departments={departments || []}
                    batchAbbreviations={existingAbbreviations}
                  />

                  <FormInput
                    control={control}
                    name="managerEmail"
                    label={t('organization.departments.manager')}
                    placeholder={t(
                      'organization.departments.bulkUploadPage.managerEmailPlaceholder'
                    )}
                    type="email"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <FieldSeparator>{t('common.Details')}</FieldSeparator>

                {/* Details Section */}
                <FormTextarea
                  control={control}
                  name="description"
                  label={t('common.Description')}
                  placeholder={t('organization.departments.descriptionPlaceholder')}
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter className="mt-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" loading={isSubmitting}>
                {isEditing ? t('common.save') : t('common.add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog for unsaved changes */}
      <UnsavedChangesConfirmDialog
        open={showCloseConfirmation}
        onStay={handleStay}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
}
